/**
 * 멤버 실사 프로필(member-profile-images) 표시용 캐시.
 * path+version 동일 → 메모리/IndexedDB Blob URL 재사용.
 * 버전이 바뀌면 재다운로드.
 */

import { useEffect, useState } from 'react';
import {
  getMemberPhotoSignedUrl,
  peekMemberPhotoSignedUrl,
} from './memberPhotoSignedUrlCache';

const DB_NAME = 'ClassPlannerMemberPhotoImageCache';
const DB_VERSION = 1;
const STORE = 'images';

type CacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryByKey = new Map<string, MemoryEntry>();
const inflightByKey = new Map<string, Promise<string | null>>();

function cacheKey(path: string, version?: number | null): string {
  const v =
    typeof version === 'number' && Number.isFinite(version) && version > 0
      ? String(Math.trunc(version))
      : '0';
  return `${path.trim()}::v${v}`;
}

function versionTag(version?: number | null): string {
  const n = Number(version);
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : '0';
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<CacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<CacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as CacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: CacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

function remember(key: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryByKey.get(key);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryByKey.set(key, { blobUrl, version });
  return blobUrl;
}

/** 동기 peek — 메모리에 같은 버전이 있으면 즉시 반환 */
export function peekMemberPhotoBlobUrl(
  path?: string | null,
  version?: number | null
): string | null {
  const trimmed = (path ?? '').trim();
  if (!trimmed) return null;
  const key = cacheKey(trimmed, version);
  const mem = memoryByKey.get(key);
  if (mem && mem.version === versionTag(version)) return mem.blobUrl;
  return null;
}

/**
 * path+version 동일 → 캐시 Blob URL.
 * miss면 signed URL로 fetch 후 IndexedDB·메모리에 저장.
 */
export async function getCachedMemberPhotoBlobUrl(
  path?: string | null,
  version?: number | null
): Promise<string | null> {
  const trimmed = (path ?? '').trim();
  if (!trimmed) return null;
  const key = cacheKey(trimmed, version);
  const ver = versionTag(version);

  const mem = memoryByKey.get(key);
  if (mem && mem.version === ver) return mem.blobUrl;

  const inflight = inflightByKey.get(key);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const cached = await idbGet(key);
      if (cached?.buffer?.byteLength && cached.version === ver) {
        return remember(key, ver, cached.buffer, cached.mime || 'image/webp');
      }

      const signed = await getMemberPhotoSignedUrl(trimmed, version ?? undefined);
      if (!signed) return null;

      const res = await fetch(signed, {
        method: 'GET',
        credentials: 'omit',
        mode: 'cors',
        cache: 'force-cache',
      });
      if (!res.ok) return null;
      const buffer = await res.arrayBuffer();
      if (!buffer.byteLength) return null;
      const mime = res.headers.get('content-type') || 'image/webp';
      try {
        await idbPut(key, { buffer, version: ver, mime });
      } catch {
        /* ignore quota */
      }
      return remember(key, ver, buffer, mime);
    } catch {
      return null;
    } finally {
      inflightByKey.delete(key);
    }
  })();

  inflightByKey.set(key, task);
  return task;
}

/** 표시용 URL — Blob 캐시 우선, 없으면 signed URL */
export async function getMemberPhotoDisplayUrl(
  path?: string | null,
  version?: number | null
): Promise<string | null> {
  const blob = await getCachedMemberPhotoBlobUrl(path, version);
  if (blob) return blob;
  return getMemberPhotoSignedUrl(path, version ?? undefined);
}

/**
 * 멤버 사진 표시 훅 — 메모리/세션 hit면 즉시, IDB·네트워크는 백그라운드 갱신.
 */
export function useMemberPhotoDisplayUrl(
  path?: string | null,
  version?: number | null
): string | null {
  const trimmed = (path ?? '').trim();
  const [url, setUrl] = useState<string | null>(() => {
    if (!trimmed) return null;
    return (
      peekMemberPhotoBlobUrl(trimmed, version) ??
      peekMemberPhotoSignedUrl(trimmed, version ?? undefined)
    );
  });

  useEffect(() => {
    if (!trimmed) {
      setUrl(null);
      return;
    }
    const peekedBlob = peekMemberPhotoBlobUrl(trimmed, version);
    const peekedSigned = peekMemberPhotoSignedUrl(trimmed, version ?? undefined);
    if (peekedBlob) setUrl(peekedBlob);
    else if (peekedSigned) setUrl(peekedSigned);
    else setUrl(null);

    let cancelled = false;
    void getMemberPhotoDisplayUrl(trimmed, version).then((next) => {
      if (!cancelled && next) setUrl(next);
    });
    return () => {
      cancelled = true;
    };
  }, [trimmed, version]);

  return url;
}

/** 여러 멤버 사진을 백그라운드 프리로드 */
export function preloadMemberPhotoImages(
  entries: Array<{ path?: string | null; version?: number | null }>
): void {
  const seen = new Set<string>();
  for (const e of entries) {
    const path = (e.path ?? '').trim();
    if (!path) continue;
    const key = cacheKey(path, e.version);
    if (seen.has(key)) continue;
    seen.add(key);
    void getMemberPhotoDisplayUrl(path, e.version);
  }
}
